import { ApolloError, gql, useQuery } from '@apollo/client';

import { buildSchemaLeniently } from 'src/lib/buildSchemaLeniently';
import {
  buildSubgraphMetaHelpers,
  subgraphMetaEntitiesFragment,
  subgraphMetaSubgraphsFragment,
} from 'src/lib/graphql/subgraphMetadataHelpers';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
interface Props {
  graphId?: string;
  graphVariant?: string;
}

export interface UsePublishedSubgraphsHookResult {
  loading: Boolean;
  error?: ApolloError;
  subgraphMetaHelpers?: ReturnType<typeof buildSubgraphMetaHelpers>;
}

/**
 * A convenience hook to retrieve information about the
 * subgraphs for a graph / variant, including extra
 * metadata helpers to further classify subgraphs
 * (e.g. whether they have connectors, type / field ownerships, etc.).
 */
export const usePublishedSubgraphs = ({
  graphId,
  graphVariant,
}: Props): UsePublishedSubgraphsHookResult => {
  const { data, loading, error } = useQuery<
    GraphQLTypes.PublishedSubgraphsQuery,
    GraphQLTypes.PublishedSubgraphsQueryVariables
  >(
    gql`
      query PublishedSubgraphsQuery($graphId: ID!, $graphVariant: String!) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            activeSchemaPublish {
              id
              schema {
                hash
                createdAt
                document
              }
              compositionResult {
                graphCompositionID
                ...SubgraphMetaSubgraphsFragment
              }
            }
            sourceVariant {
              id
              activeSchemaPublish {
                id
                compositionResult {
                  graphCompositionID
                  ...SubgraphMetaSubgraphsFragment
                }
              }
            }
            entities {
              ...SubgraphMetaEntitiesFragment
            }
          }
        }
      }
      ${subgraphMetaSubgraphsFragment}
      ${subgraphMetaEntitiesFragment}
    `,
    {
      variables: { graphId: graphId ?? '', graphVariant: graphVariant ?? '' },
      skip: !graphId || !graphVariant,
    },
  );

  if (loading) return { loading: true };
  if (error) return { loading: false, error };

  const schemaDocument =
    data?.service?.variant?.activeSchemaPublish?.schema.document;
  const schema = schemaDocument
    ? buildSchemaLeniently(schemaDocument).schema
    : null;

  let subgraphConfigs;
  if (data?.service?.variant?.sourceVariant) {
    subgraphConfigs =
      data?.service?.variant?.sourceVariant?.activeSchemaPublish
        ?.compositionResult?.subgraphConfigs;
  } else {
    subgraphConfigs =
      data?.service?.variant?.activeSchemaPublish?.compositionResult
        ?.subgraphConfigs;
  }

  const entities =
    data?.service?.variant?.entities?.__typename === 'EntitiesResponse'
      ? data.service.variant.entities.entities
      : undefined;

  return {
    loading: false,
    subgraphMetaHelpers: buildSubgraphMetaHelpers(
      entities,
      subgraphConfigs,
      schema,
    ),
  };
};
